import { askAIQuestion, logBotMessage } from '../utils/api';
import { saveConversationToStorage } from '../utils/storage';
import {
  filterNavigablePlaces,
  buildPlacesResponse,
  buildTextResponse,
  buildDeferredExternalMessage,
  buildAdditionalInfoMessage,
  paginatePlaces,
  nextMessageId,
} from '../utils/aiResponseProcessor';
import {
  buildNextStepMessage,
  buildMorePlacesMessage,
} from '../utils/conversationFlow';
import { CONTINUE_TOUR_BUTTON_TEXT, buildMessageHistory } from '../utils/helpers';

const POST_AI_FOLLOWUP_DELAY = 5000;
const POST_TEXT_FOLLOWUP_DELAY = 10000;
const EXTERNAL_FOLLOWUP_DELAY = 10000;
const SINGLE_PLACE_AUTO_SELECT_DELAY = 5000;

const useAdditionalInterests = ({
  state,
  dispatch,
  communityNid,
  communityName,
  recordActivity,
  scheduleDeferredMessage,
  onExternalLocationFound,
  onAmenitySelected,
  onResetToCommunity,
  onEnsureZoomZoneZoom,
}) => {
  const handleAdditionalInterestsSubmit = async () => {
    const {
      additionalInterests,
      messages,
      conversationState,
      sessionId,
      formData,
      amenityPlaces,
    } = state;
    const userInput = additionalInterests.trim();

    const context = {
      sessionId,
      selectedInterests: conversationState.selectedInterests.map((i) => i.uuid),
      userInfo: conversationState.userInfo,
      additionalInterests: conversationState.additionalInterests || userInput,
      communityNid,
      communityName,
      currentStep: 'step4_followup',
      isFirstAIQuestion: false,
      shownPlaceUuids: amenityPlaces.map((p) => p.uuid).filter(Boolean),
      messageHistory: buildMessageHistory(messages),
    };

    const logContext = {
      sessionId,
      selectedInterests: conversationState.selectedInterests.map((i) => i.uuid),
      userInfo: conversationState.userInfo,
      communityNid,
    };

    // Kick off the network request before any dispatches so it overlaps
    // with React's render work and the pacing delays below.
    const aiResponsePromise = askAIQuestion(userInput, context);

    recordActivity();

    const userMessage = {
      id: nextMessageId(),
      type: 'user',
      text: userInput,
    };
    const messagesWithUser = [...messages, userMessage];
    dispatch({ type: 'SET_MESSAGES', payload: messagesWithUser });
    dispatch({ type: 'SET_FLOW_STEP', payload: 5 });
    dispatch({ type: 'SET_ADDITIONAL_INTERESTS', payload: '' });

    dispatch({ type: 'SET_IS_AI_MODE', payload: true });

    const isFirstAIQuestion = !conversationState.additionalInterests;

    if (!conversationState.additionalInterests) {
      dispatch({
        type: 'SET_CONVERSATION_STATE',
        payload: { additionalInterests: userInput },
      });
    }

    try {
      dispatch({ type: 'SET_IS_AI_THINKING', payload: { active: true, withLabel: true } });
      const aiResponse = await aiResponsePromise;
      const firstName = conversationState.userInfo?.firstName || '';

      if (aiResponse.showPlaceButtons && aiResponse.places) {
        const navigablePlaces = filterNavigablePlaces(aiResponse.places);

        if (navigablePlaces.length > 0) {
          const botResponse = buildPlacesResponse(aiResponse, navigablePlaces, firstName, { communityName });
          botResponse.continueButton = { text: 'Or, Continue To Next Tour Stop', action: 'flush_deferred' };
          const { remaining } = paginatePlaces(navigablePlaces);
          const newMessages = [...messagesWithUser, botResponse];

          dispatch({ type: 'SET_MESSAGES', payload: newMessages });
          dispatch({ type: 'APPEND_AMENITY_PLACES', payload: navigablePlaces });
          dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
          dispatch({ type: 'SET_FLOW_STEP', payload: 6 });
          recordActivity();
          // Fire-and-forget: localStorage write off the critical path.
          saveConversationToStorage(
            sessionId,
            newMessages,
            conversationState,
            formData,
            amenityPlaces,
          );

          // Always show internal places first, then defer external locations.
          // Collect external locations from either format the API may return.
          const externalLocs = aiResponse.deferredExternal?.externalLocations
            || aiResponse.externalLocations
            || [];
          const deferredResponse = aiResponse.deferredExternal?.response
            || aiResponse.response;

          // Auto-select when only one place is returned.
          if (navigablePlaces.length === 1 && onAmenitySelected) {
            setTimeout(() => {
              onAmenitySelected(navigablePlaces[0], { openModal: false });
            }, SINGLE_PLACE_AUTO_SELECT_DELAY);
          } else if (onResetToCommunity) {
            onResetToCommunity();
          }

          // Build a chain of deferred follow-up messages. Each fires after
          // inactivity, then schedules the next one. External is queued
          // first so the "Or, Continue To Next Tour Stop" button (action:
          // flush_deferred) surfaces it on demand.
          const followUpChain = [];

          if (externalLocs.length > 0) {
            followUpChain.push({
              delay: EXTERNAL_FOLLOWUP_DELAY,
              fn: () => {
                const extMsg = buildDeferredExternalMessage({
                  externalLocations: externalLocs,
                  response: deferredResponse,
                });
                dispatch({ type: 'APPEND_MESSAGES', payload: [extMsg], deferred: true });
                logBotMessage(deferredResponse || '', {
                  sessionId,
                  selectedInterests: conversationState.selectedInterests.map((i) => i.uuid),
                  userInfo: conversationState.userInfo,
                  communityNid,
                  currentStep: 'step4_followup',
                  questionType: 'deferred_external',
                });
                recordActivity();
              },
            });
          }

          if (aiResponse.additionalInfo) {
            followUpChain.push({
              fn: () => {
                const infoMsg = buildAdditionalInfoMessage(aiResponse.additionalInfo);
                dispatch({ type: 'APPEND_MESSAGES', payload: [infoMsg], deferred: true });
              },
            });
          }

          if (remaining.length > 0) {
            const interests = conversationState.selectedInterests.map((i) => i.label).join(', ') || userInput;
            dispatch({ type: 'SET_PENDING_PLACES', payload: { places: remaining, interests } });
            followUpChain.push({
              fn: () => {
                const firstNameForPrompt = conversationState.userInfo?.firstName || '';
                const moreMsg = buildMorePlacesMessage(interests, firstNameForPrompt, { communityName });
                dispatch({ type: 'APPEND_MESSAGES', payload: [moreMsg], deferred: true });
                if (moreMsg?.text) {
                  logBotMessage(moreMsg.text, { ...logContext, currentStep: 'more_places', questionType: 'more_places_prompt' });
                }
              },
            });
          } else {
            dispatch({ type: 'CLEAR_PENDING_PLACES' });
            followUpChain.push({
              fn: () => {
                const firstNameForPrompt = conversationState.userInfo?.firstName || '';
                const msg = buildNextStepMessage(firstNameForPrompt, { communityName });
                if (msg) {
                  dispatch({ type: 'APPEND_MESSAGES', payload: [msg], deferred: true });
                  dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
                  logBotMessage(msg.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
                }
              },
            });
          }

          // Run chain sequentially: each step defers the next after inactivity.
          const runFollowUpChain = (index) => {
            if (index >= followUpChain.length) return;
            const { fn, delay } = followUpChain[index];
            scheduleDeferredMessage(() => {
              fn();
              runFollowUpChain(index + 1);
            }, delay);
          };
          runFollowUpChain(0);
        } else {
          // No internal places — show external locations button without
          // auto-triggering so the user can click it themselves.
          const externalLocsOnly = aiResponse.deferredExternal?.externalLocations
            || aiResponse.externalLocations
            || [];
          if (externalLocsOnly.length > 0) {
            const seeText = externalLocsOnly.length > 1 ? 'Area Locations' : 'Area Location';
            const botResponse = {
              id: nextMessageId(),
              type: 'bot',
              text: aiResponse.deferredExternal?.response || aiResponse.response || '',
              instructions: externalLocsOnly.length > 1
                ? 'Click below to view locations on the map:'
                : 'Click below to view on the map:',
              buttons: [{
                text: seeText,
                action: 'see_external_locations',
                externalLocations: externalLocsOnly,
              }],
              continueButton: { text: CONTINUE_TOUR_BUTTON_TEXT, action: 'flush_deferred' },
            };
            const newMessages = [...messagesWithUser, botResponse];
            dispatch({ type: 'SET_MESSAGES', payload: newMessages });
            dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
            saveConversationToStorage(
              sessionId,
              newMessages,
              conversationState,
              formData,
              amenityPlaces,
            );
            scheduleDeferredMessage(() => {
              const msg = buildNextStepMessage(firstName, { communityName });
              if (msg) {
                dispatch({ type: 'APPEND_MESSAGES', payload: [msg], deferred: true });
                dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
                logBotMessage(msg.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
              }
            }, POST_TEXT_FOLLOWUP_DELAY);
          } else {
            dispatch({ type: 'SET_MESSAGES', payload: messagesWithUser });

            setTimeout(() => {
              dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
              const msg = buildNextStepMessage(firstName, { communityName });
              if (msg) {
                dispatch({ type: 'APPEND_MESSAGES', payload: [msg], deferred: true });
                dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
                logBotMessage(msg.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
              }
            }, POST_TEXT_FOLLOWUP_DELAY);
          }
        }
      } else if (aiResponse.response) {
        const botResponse = buildTextResponse(aiResponse);
        const firstName = conversationState.userInfo?.firstName || '';

        // Text-only AI responses are about the community itself (no
        // navigable places). Ensure the map is zoomed in at least to the
        // zoom-zone activation level.
        if (onEnsureZoomZoneZoom) {
          onEnsureZoomZoneZoom();
        }

        const hasExternalLocations = aiResponse.externalLocations?.length > 0;

        if (hasExternalLocations) {
          const seeText = aiResponse.externalLocations.length > 1
            ? 'Area Locations'
            : 'Area Location';
          botResponse.instructions = aiResponse.externalLocations.length > 1
            ? 'Click below to view locations on the map:'
            : 'Click below to view on the map:';
          botResponse.buttons = [{
            text: seeText,
            action: 'see_external_locations',
            externalLocations: aiResponse.externalLocations,
          }];
          botResponse.continueButton = { text: CONTINUE_TOUR_BUTTON_TEXT, action: 'flush_deferred' };
        } else {
          // Inline the next_step options on the AI response itself instead
          // of a deferred next_step prompt.
          const nextStepMsg = buildNextStepMessage(firstName, { communityName });
          if (nextStepMsg) {
            botResponse.instructions = nextStepMsg.instructions;
            botResponse.buttons = nextStepMsg.buttons;
            botResponse.stepType = 'next_step_prompt';
          }
        }

        const newMessages = [...messagesWithUser, botResponse];

        dispatch({ type: 'SET_MESSAGES', payload: newMessages });
        dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
        // Fire-and-forget: localStorage write off the critical path.
        saveConversationToStorage(
          sessionId,
          newMessages,
          conversationState,
          formData,
          amenityPlaces,
        );
        if (hasExternalLocations) {
          scheduleDeferredMessage(() => {
            const nextStepMsg = buildNextStepMessage(firstName, { communityName });
            if (nextStepMsg) {
              dispatch({ type: 'APPEND_MESSAGES', payload: [nextStepMsg], deferred: true });
              dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
              logBotMessage(nextStepMsg.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
            }
          }, POST_TEXT_FOLLOWUP_DELAY);
        } else {
          dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
        }
      } else {
        dispatch({ type: 'SET_MESSAGES', payload: messagesWithUser });

        setTimeout(() => {
          dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
          const msg = buildNextStepMessage(conversationState?.userInfo?.firstName || '', { communityName });
          if (msg) {
            dispatch({ type: 'APPEND_MESSAGES', payload: [msg], deferred: true });
            dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
            logBotMessage(msg.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
          }
        }, POST_AI_FOLLOWUP_DELAY);
      }

      if (isFirstAIQuestion) {
        dispatch({
          type: 'SET_CONVERSATION_STATE',
          payload: { currentStep: 'step4_followup' },
        });
      }
    } catch (error) {
      console.error('[TourGuideAi] Error getting AI response:', error);
      dispatch({ type: 'SET_IS_AI_THINKING', payload: false });

      const newMessages = [...messagesWithUser,
        {
          id: nextMessageId() + 1,
          type: 'bot',
          text: "I apologize, but I'm having trouble processing your question right now. Please try again later or speak with us for assistance.",
        }];
      dispatch({ type: 'SET_MESSAGES', payload: newMessages });
      saveConversationToStorage(
        sessionId,
        newMessages,
        conversationState,
        formData,
        amenityPlaces,
      );

      if (isFirstAIQuestion) {
        dispatch({
          type: 'SET_CONVERSATION_STATE',
          payload: { currentStep: 'step4_followup' },
        });
      }
    }
  };

  return { handleAdditionalInterestsSubmit };
};

export default useAdditionalInterests;
